/*
 * Purpose: Mission map flow actions — check, run, save (first-class sub-nav items)
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Play,
  Save,
  Square,
  ClipboardCheck,
  Pencil,
  Copy,
  Share2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { WorkflowCloneDialog } from '@/components/WorkflowCloneDialog';
import { useWorkflowStore } from '@/stores/workflowStore';
import { WorkflowValidator } from '@/services/validation';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { missionStartBlockedReason, isMapLocked } from '@/lib/missionRules';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { toastContextualIssue } from '@/lib/contextualIssuePolicy';
import { toast } from 'sonner';
import { useMissionWorkspace } from '@/contexts/MissionWorkspaceContext';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';
import { cn } from '@/lib/utils';

const navBtn =
  'h-8 px-2.5 sm:px-3 rounded-md text-xs sm:text-sm font-medium inline-flex items-center gap-1.5 shrink-0 transition-colors';

export function MissionFlowControls({
  className,
  inverted = false,
}: {
  className?: string;
  inverted?: boolean;
}) {
  const { runtime } = useMissionWorkspace();
  const { canSave, canRun, message } = useSubscriptionGate();
  const { currentWorkflow, saveWorkflow, updateWorkflowName, updateMissionMeta, selectNode } =
    useWorkflowStore();
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [cloneOpen, setCloneOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);

  const isExecuting = runtime?.isExecuting ?? false;
  const onExecute = runtime?.onExecute;
  const onStop = runtime?.onStop;
  const onOpenAssistant = runtime?.onOpenAssistant;

  const validation = useMemo(() => {
    if (!currentWorkflow) return [];
    return WorkflowValidator.validate(currentWorkflow);
  }, [currentWorkflow]);

  const errorCount = validation.filter((e) => e.type === 'error').length;
  const warningCount = validation.filter((e) => e.type === 'warning').length;
  const startBlocked = missionStartBlockedReason(currentWorkflow);
  const startTitle =
    startBlocked ?? (errorCount > 0 ? 'Fix check issues before starting' : undefined);
  const mapLocked = isMapLocked(currentWorkflow);

  const handleMapLockChange = useCallback(
    (locked: boolean) => {
      updateMissionMeta({ mapLocked: locked, allowDynamicMap: !locked });
      saveWorkflow();
      toast.message(
        locked ? 'MapLock on — map stays fixed during runs' : 'Map unlocked — AI may replan mid-run'
      );
    },
    [updateMissionMeta, saveWorkflow]
  );

  const handleSave = useCallback(() => {
    saveWorkflow();
    toast.success(`${PRODUCT_TERMS.autoBlockFlow} saved`);
  }, [saveWorkflow]);

  const openEdit = useCallback(() => {
    if (!currentWorkflow) return;
    setEditName(currentWorkflow.name);
    setEditOpen(true);
  }, [currentWorkflow]);

  const handleRename = useCallback(() => {
    if (!editName.trim()) {
      toast.error('Enter a flow name', {
        action: {
          label: 'Focus name',
          onClick: () => document.getElementById('flow-name')?.focus(),
        },
      });
      return;
    }
    updateWorkflowName(editName);
    saveWorkflow();
    toast.success('Flow renamed');
    setEditOpen(false);
  }, [editName, updateWorkflowName, saveWorkflow]);

  const handleShare = useCallback(async () => {
    if (!currentWorkflow) return;
    const url = `${window.location.origin}${REMEDIATION.mission}?flow=${currentWorkflow.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch {
      toastContextualIssue('Could not copy link', {
        label: 'Retry',
        onClick: () => void handleShare(),
      });
    }
  }, [currentWorkflow]);

  if (!runtime) return null;

  return (
    <>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1 sm:gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1',
          className
        )}
        role="toolbar"
        aria-label="Flow actions"
        {...(inverted ? { 'data-mission-tool': true } : {})}
      >
        {currentWorkflow && (
          <span
            className={cn(
              'hidden lg:inline text-[11px] mr-1 shrink-0',
              inverted ? 'ab-mission-tool-meta' : 'text-muted-foreground'
            )}
          >
            {currentWorkflow.nodes.length} blocks · {currentWorkflow.edges.length} links
          </span>
        )}

        {!currentWorkflow ? null : isExecuting ? (
          <Badge variant="secondary" className="shrink-0 text-[10px] h-6">
            Running
          </Badge>
        ) : errorCount > 0 ? (
          <button
            type="button"
            className="inline-flex shrink-0"
            onClick={() => setCheckOpen(true)}
            aria-label={`View ${errorCount} issues`}
          >
            <Badge variant="destructive" className="text-[10px] h-6 cursor-pointer">
              {errorCount} issue{errorCount === 1 ? '' : 's'}
            </Badge>
          </button>
        ) : warningCount > 0 ? (
          <button
            type="button"
            className="inline-flex shrink-0"
            onClick={() => setCheckOpen(true)}
            aria-label={`View ${warningCount} warnings`}
          >
            <Badge
              variant="outline"
              className="text-[10px] h-6 border-amber-500/50 text-amber-700 dark:text-amber-400"
            >
              {warningCount} warn
            </Badge>
          </button>
        ) : (
          <Badge
            variant="outline"
            className="shrink-0 text-[10px] h-6 border-green-500/40 text-green-700 dark:text-green-400"
          >
            Ready
          </Badge>
        )}

        <Popover open={checkOpen} onOpenChange={setCheckOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              disabled={!currentWorkflow}
              className={navBtn}
              data-flow-check-trigger
            >
              <ClipboardCheck className="w-4 h-4 shrink-0" />
              Check
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="start">
            <p className="text-sm font-medium mb-2">Flow check</p>
            {validation.length === 0 ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 ui-icon-ok shrink-0" />
                All checks passed — safe to run.
              </p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-auto">
                {validation.map((item, idx) => (
                  <li key={`${item.message}-${idx}`} className="text-xs flex items-start gap-2">
                    {item.type === 'error' ? (
                      <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5 ui-icon shrink-0 mt-0.5" />
                    )}
                    <span className="flex-1">{item.message}</span>
                    {item.nodeId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[10px]"
                        onClick={() => {
                          selectNode(item.nodeId!);
                          setCheckOpen(false);
                        }}
                      >
                        Go
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </PopoverContent>
        </Popover>

        <Button size="sm" variant="ghost" disabled={!currentWorkflow} className={navBtn} onClick={openEdit}>
          <Pencil className="w-4 h-4 shrink-0" />
          Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!currentWorkflow}
          className={navBtn}
          onClick={() => setCloneOpen(true)}
        >
          <Copy className="w-4 h-4 shrink-0" />
          Duplicate
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!currentWorkflow}
          className={navBtn}
          onClick={() => void handleShare()}
        >
          <Share2 className="w-4 h-4 shrink-0" />
          Share
        </Button>

        {currentWorkflow && (
          <div
            className={cn(
              navBtn,
              'border border-border/80 bg-muted/30',
              mapLocked && 'border-primary/40 bg-primary/5'
            )}
            title={
              mapLocked
                ? 'MapLock on — map cannot change during a run'
                : 'Map unlocked — AI may replan mid-run'
            }
          >
            <Lock className={cn('w-3.5 h-3.5 shrink-0', mapLocked && 'text-primary')} />
            <Label htmlFor="mission-map-lock" className="text-xs cursor-pointer whitespace-nowrap">
              MapLock
            </Label>
            <Switch
              id="mission-map-lock"
              checked={mapLocked}
              disabled={isExecuting}
              onCheckedChange={handleMapLockChange}
              aria-label="MapLock"
            />
          </div>
        )}

        {onOpenAssistant && (
          <Button size="sm" variant="ghost" className={navBtn} onClick={onOpenAssistant}>
            <Sparkles className="w-4 h-4 shrink-0 text-primary" />
            AI Assistant
          </Button>
        )}

        <span className="flex-1 min-w-[4px]" aria-hidden />

        <Button
          size="sm"
          variant="ghost"
          className={navBtn}
          onClick={handleSave}
          disabled={!currentWorkflow || isExecuting || !canSave}
          title={!canSave ? message('save_flows') : undefined}
        >
          <Save className="w-4 h-4 shrink-0" />
          Save
        </Button>

        {isExecuting ? (
          <Button size="sm" variant="destructive" className={navBtn} onClick={onStop}>
            <Square className="w-4 h-4 shrink-0 fill-current" />
            Stop
          </Button>
        ) : (
          <Button
            size="sm"
            actionTier="primary"
            className={navBtn}
            onClick={() => void onExecute?.()}
            disabled={!currentWorkflow || errorCount > 0 || !canRun}
            title={!canRun ? message('run_flows') : startTitle}
          >
            <Play className="w-4 h-4 shrink-0" />
            Start
          </Button>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {PRODUCT_TERMS.autoBlockFlow}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="flow-name">Flow name</Label>
            <Input
              id="flow-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="ui-form-control"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button actionTier="ghost" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button actionTier="primary" onClick={handleRename}>
              Save name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {currentWorkflow && (
        <WorkflowCloneDialog
          workflowId={currentWorkflow.id}
          workflowName={currentWorkflow.name}
          open={cloneOpen}
          onOpenChange={setCloneOpen}
        />
      )}
    </>
  );
}
